import { ChangeEvent, FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { debounce } from 'throttle-debounce';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
// Hooks
import useToggle from 'app/hooks/useToggle';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Types
import CaseStatuses from 'app/types/CaseStatuses';
import SubscriptionItemServiceCodes from 'app/types/SubscriptionItemServiceCodes';
// Models
import { RootState } from 'app/store';
// Async
import { getInvoices } from 'app/store/BillingInvoices/BillingInvoices.async';
import { getInsuranceCases } from 'app/store/Cases/Cases.async';
// Actions
import { BillingInvoicesActions } from 'app/store/BillingInvoices/BillingInvoices.slice';
// Selectors
import { selectAccountsAsOptions } from 'app/store/Clients/Clients.selectors';
import { selectParams, selectValidateDateRange } from 'app/store/BillingInvoices/BillingInvoices.selectors';
import { selectCaseOptions } from 'app/store/Cases/Cases.selectors';
import { selectSubscriptionItemByCode } from 'app/store/BillingSubscriptions/BillingSubscriptions.selectors';
import { selectSplitInvoicePerCaseEnabled } from 'app/store/Accounts/Accounts.selectors';
// Mui
import {
  Autocomplete as MuiAutocomplete, Box, Theme, Typography,
  TextField, CircularProgress, FormHelperText, Divider
} from '@mui/material';
// Components
import { DesktopDatePicker, Input, Select } from 'app/components/Mui';
import { Autocomplete } from 'app/components/Mui/Autocompletes';
import QuickDateChips from 'app/components/QuickDateChips';
import SidebarToggleButton from 'app/components/SidebarToggleButton';
// Utilities
import { getSearchParam } from 'app/utilities/Utilities';
import { adjustDateRange } from 'app/utilities/DateTime';
import { DATA_LIMIT, SIDEBAR_WIDTH } from 'app/App.constants';
// Mixpanel
import MixpanelActions from 'app/features/mixpanel/MixpanelActions';

const BillingInvoicesFilter:FC = () => {
  const { t } = useTranslation();

  const { search:searchLocaltion } = useLocation();
  
  const invoiceNumber = getSearchParam(searchLocaltion, 'invoiceNumber');
  const dateRangeStart = getSearchParam(searchLocaltion, 'dateRangeStart');
  const dateRangeEnd = getSearchParam(searchLocaltion, 'dateRangeEnd');

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const accountOptions = useAppSelector(selectAccountsAsOptions)
  const caseOptions = useAppSelector(selectCaseOptions);
  const splitInvoicePerCaseEnabled = useAppSelector(selectSplitInvoicePerCaseEnabled);
  const subscriptionItem = useAppSelector((state:RootState) => selectSubscriptionItemByCode(state, { code: SubscriptionItemServiceCodes.PageImport }));
  const validationErrors = useAppSelector((state:RootState) => selectValidateDateRange(state, { isRequired: false }));
  const params:any = useAppSelector(selectParams);

  const [ search, setSearch ] = useState(invoiceNumber || '');

  const formatDate = (date:Dayjs | null, period:'start' | 'end') => {
    if (!date) return null;
    return date.add(period === 'start' ? 0 : 1, 'day').format('YYYY-MM-DD');
  }

  const handleMixpanelEvent = (field:keyof typeof params, value:any) => {
    if ( field === 'search' ) {
      dispatch(MixpanelActions.handleBillingInvoicesSearchFilterClick({ search: value }));
    }
    if ( field === 'status' ) {
      dispatch(MixpanelActions.handleBillingInvoicesStatusFilterClick({ status: value }));
    }
    if ( field === 'case' ) {
      dispatch(MixpanelActions.handleBillingInvoicesCaseFilterClick({ caseId: value?.id || null }));
    }
  }

  // eslint-disable-next-line
  const debounceParams = useCallback(debounce(500, (field:keyof typeof params, value:any) => {
    dispatch(BillingInvoicesActions.setParams({ [field]: value, offset: 0 }));

    handleMixpanelEvent(field, value);

    dispatch(BillingInvoicesActions.setInitialField('batchInvoices'));
  }), [params]);

  // eslint-disable-next-line
  const debouncePeriodParams = useCallback(debounce(1000, (period: 'start' | 'end', value: Dayjs | null, oppositeValue: Dayjs | null) => {
    const { start, end } = adjustDateRange(period, value, oppositeValue);

    dispatch(BillingInvoicesActions.setParams({ dateRangeStart: start, dateRangeEnd: end, offset: 0 }));

    dispatch(MixpanelActions.handleBillingInvoicesDateFilterClick({ start: formatDate(start, 'start'), end: formatDate(end, 'end') }));

    dispatch(BillingInvoicesActions.setInitialField('batchInvoices'));
    // eslint-disable-next-line
  }), [params]);

  useEffect(() => {
    const invoiceParams = { ...params };

    if (invoiceNumber) {
      invoiceParams['search'] = invoiceNumber;
      invoiceParams['status'] = 'paid,sent';
    }

    if (dateRangeStart) invoiceParams['dateRangeStart'] = dayjs(dateRangeStart);
    if (dateRangeEnd) invoiceParams['dateRangeEnd'] = dayjs(dateRangeEnd);

    dispatch(BillingInvoicesActions.setParams(invoiceParams));
    dispatch(getInvoices({
      ...invoiceParams,
      dateRangeStart: formatDate(invoiceParams.dateRangeStart, 'start'),
      dateRangeEnd: formatDate(invoiceParams.dateRangeEnd, 'end')
    }));
    // eslint-disable-next-line
  }, []);

  const handleAccountChange = (value:any) => {
    debounceParams('accountId', value);
    dispatch(MixpanelActions.handleBillingInvoicesClientFilterClick({ clientAccountId: value }));
  }

  const handleChange = (event:ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if ( name === 'search' ) setSearch(value);
    debounceParams(name as any, value);
  }

  const handleChangeStatus = (event:ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    const status = value === 'overdue' ? 'sent' : value;

    const updates:Record<string, any> = {
      status,
      overdue: value === 'overdue' ? true : null
    };

    dispatch(BillingInvoicesActions.setParams({ ...updates, offset: 0 }));

    dispatch(MixpanelActions.handleBillingInvoicesStatusFilterClick({ status }));
  }

  const statusValue = useMemo(() => {
    if (params.overdue && params.status === 'sent') return 'overdue';
    return params.status;
  }, [params.status, params.overdue]);

  // eslint-disable-next-line
  const debounceGetCases = useCallback(debounce(500, (search:string) => {
    dispatch(getInsuranceCases({
      offset: 0,
      limit: DATA_LIMIT,
      search,
      status: `${CaseStatuses.Open},${CaseStatuses.Archived}`
    }))
  }), []);

  useEffect(() => {
    debounceGetCases('');
    // eslint-disable-next-line
  }, []);

  const handleChangeCase = (_:any, newValue:any) => {
    debounceParams('case', newValue);
  }

  const handleInputChange = (_:any, value:string) => {
    debounceGetCases(value);
  }

  const handleDateChange = (period:'start' | 'end') => (newValue:Dayjs | null) => {
    const opositeValue = period === 'start' ? params.dateRangeEnd : params.dateRangeStart;
    debouncePeriodParams(period, newValue, opositeValue);
  };

  const handleDateRangeChange = (start: Dayjs | null, end: Dayjs | null) => {
    dispatch(BillingInvoicesActions.setParams({ dateRangeStart: start, dateRangeEnd: end, offset: 0 }));

    dispatch(MixpanelActions.handleBillingInvoicesDateFilterClick({ start: formatDate(start, 'start'), end: formatDate(end, 'end') }));
  };
  
  const handleClear = () => {
    setSearch('');
    debounceParams('search', '');
  }

  const statusOptions = [
    { id: 'paid,sent', name: t('labels.all') },
    { id: 'paid', name: t('labels.paid') },
    { id: 'sent', name: t('labels.outstanding') },
    { id: 'overdue', name: `${t('labels.outstanding')} (${t('labels.overdue')})` }
  ];

  const { open, toggle } = useToggle(true);

  const initialCaseOptions = caseOptions || [];
  const caseLoading = !initialCaseOptions;
  const isRecurring = subscriptionItem?.recurring;
  const showCaseFilter = !isRecurring || splitInvoicePerCaseEnabled;

  return (
    <Box
      // Data attribute which exclude block from print using CTRL/CMD + P
      data-print="exclude"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        bgcolor: 'white',
        borderRight: (theme:Theme) => `1px solid ${theme.palette.divider}`,
        zIndex: 1,
        marginLeft: open ? 0 : `-${SIDEBAR_WIDTH}px`,
        transition: 'margin-left 0.3s ease',
      }}
    >
      <Box sx={{
        flexGrow: 1,
        py: 2,
        px: 4,
        overflowY: 'auto'
      }}>
        <Autocomplete
          value={params.accountId}
          onChange={handleAccountChange}
          options={accountOptions || []}
          TextFieldProps={{
            label: t('labels.clientAccount')
          }}
          renderOption={(props, option) => (
            <li {...props} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }} key={option.value}>
              <Typography variant="subtitle1">{option.label}</Typography>
              {option.props.address ? (
                <Typography variant="caption">{option.props.address}</Typography>
              ) : null}
            </li>
          )}
        />
        <Input
          label={t('labels.search')} name="search" value={search}
          placeholder={t('form.placeholder.caseClaimOrInvoiceNumber')}
          onChange={handleChange}
          onClear={handleClear}
        />
        <Select
          label={t('labels.status')} id="status" name="status" value={statusValue}
          onChange={handleChangeStatus}
          options={statusOptions}
        />
        {showCaseFilter ? (
          <MuiAutocomplete
            value={params['case']}
            onChange={handleChangeCase}
            onInputChange={handleInputChange}
            options={caseOptions || []}
            loading={caseLoading}
            fullWidth
            renderInput={(params:any) => (
              <TextField
                {...params}
                label={t('form.placeholder.findCase')}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <Fragment>
                        {caseLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </Fragment>
                    ),
                  }
                }}
                margin="normal"
                fullWidth
              />
            )}
            renderOption={(props:any, option:any) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
            getOptionLabel={(option:any) => option.name}
            isOptionEqualToValue={(option:any, value:any) => option.id === value.id}
          />
        ) : null}
        <Divider sx={{ mt: 2, mx: -4 }} />
        <DesktopDatePicker
          label={t('labels.start')} name="dateRangeStart" value={params['dateRangeStart']}
          onChange={handleDateChange('start')}
          slotProps={{
            textField: {
              error: Boolean(validationErrors && validationErrors.startError),
              helperText: validationErrors?.startError || ''
            }
          }}
        />
        <DesktopDatePicker
          label={t('labels.end')} name="dateRangeEnd" value={params['dateRangeEnd']}
          onChange={handleDateChange('end')}
          slotProps={{
            textField: {
              error: Boolean(validationErrors && validationErrors.endError),
              helperText: validationErrors?.endError || ''
            }
          }}
        />
        {validationErrors && validationErrors.rangeError ? (
          <FormHelperText error>{validationErrors.rangeError}</FormHelperText>
        ) : null}
        <QuickDateChips onChange={handleDateRangeChange} />
      </Box>
      <SidebarToggleButton
        name="Billing invoices sidebar toggle button"
        open={open}
        onClick={toggle}
      />
    </Box>
  )
}

export default BillingInvoicesFilter;
